import React from 'react';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Backdrop, CircularProgress, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import _ from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import ChangeUserRolePopup from 'src/admin/screens/UsersScreen/components/ChangeUserRolePopup';

import {
  useDisableUserMutation,
  useEnableUserMutation,
} from '../../../common/apis/AdminApi';
import { ReactComponent as StarIconBlue } from '../../../common/assets/icons/star_icon_blue.svg';
import UserIconOrange from '../../../common/assets/icons/user_icon_green.svg';
import PaginationControl from '../../../common/components/PaginationControl';
import { AuthContext } from '../../../common/components/tfa-auth/contexts/authContext';
import { Pages } from '../../../common/constants/Pages';
import { useUserRole } from '../../../common/hooks/useUserRole';
import { theme } from '../../../common/theme/theme';
import { User, UserRoles } from '../../../common/types/User';
import { displayDateString } from '../../../common/utils/DateUtils';
import { extractUserRole } from '../../../common/utils/User';
import { UserMenuOptions } from '../../constants/UserMenuOptions';
import InfoModal from '../InfoModal/InfoModal';
import PopupMenu from '../PopupMenu/PopupMenu';

import '../TableStyles.scss';

export type Order = 'asc' | 'desc';

type HeaderCell = {
  id: keyof Omit<User, 'lastKnownSubscription'>;
  label: string;
  numeric: boolean;
};

const headerCells: readonly HeaderCell[] = [
  {
    id: 'fullName',
    label: 'Full Name',
    numeric: false,
  },
  {
    id: 'email',
    label: 'Email',
    numeric: false,
  },
  {
    id: 'phone',
    label: 'Phone Number',
    numeric: false,
  },
  {
    id: 'organizationName',
    label: 'Organization',
    numeric: false,
  },
  {
    id: 'accessRoles',
    label: 'Role',
    numeric: false,
  },
  {
    id: 'keywordSearchesUsed',
    label: 'Keywords',
    numeric: true,
  },
  {
    id: 'scanSearchesUsed',
    label: 'Scans',
    numeric: true,
  },
  {
    id: 'purchasedScansAvailable',
    label: 'Available',
    numeric: true,
  },
  {
    id: 'subscription',
    label: 'Subscription',
    numeric: false,
  },
  {
    id: 'lastLogin',
    label: 'Last Login',
    numeric: false,
  },
];

interface AdminTableHeadProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Omit<User, 'lastKnownSubscription'>
  ) => void;
  order: Order;
  orderBy: string;
  userRole: UserRoles;
}

const AdminTableHead: React.FC<AdminTableHeadProps> = ({
  onRequestSort,
  order = 'desc',
  orderBy = 'lastLogin',
  userRole,
}) => {
  const createSortHandler =
    (property: keyof Omit<User, 'lastKnownSubscription'>) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const conditionedHeaderCells =
    userRole === UserRoles.SUPER_ADMIN
      ? headerCells
      : headerCells.filter(
          (row) =>
            row.id !== 'subscription' &&
            row.id !== 'purchasedScansAvailable' &&
            row.id !== 'organizationName'
        );

  return (
    <TableHead
      sx={{
        backgroundColor: (theme) => theme.palette.primaryLight?.main,
        height: 52,
      }}
    >
      <TableRow>
        {conditionedHeaderCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              hideSortIcon={headCell.id === 'accessRoles'}
              IconComponent={() => {
                if (orderBy !== headCell.id) {
                  return <KeyboardArrowUpRoundedIcon />;
                }
                return order === 'desc' ? (
                  <KeyboardArrowDownRoundedIcon />
                ) : (
                  <KeyboardArrowUpRoundedIcon />
                );
              }}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
        {userRole === UserRoles.SUPER_ADMIN && <TableCell />}
      </TableRow>
    </TableHead>
  );
};

type AdminTableProps = {
  users: User[];
  isLoading: boolean;
  total: number;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  requestSort: (
    property: keyof Omit<User, 'lastKnownSubscription'>,
    order: Order
  ) => void;
};

const UsersTable: React.FC<AdminTableProps> = ({
  users,
  isLoading,
  total,
  page,
  setPage,
  rowsPerPage,
  requestSort,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] =
    React.useState<keyof Omit<User, 'lastKnownSubscription'>>('lastLogin');
  const [disableUserTrigger] = useDisableUserMutation();
  const [enableUserTrigger] = useEnableUserMutation();
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [selectedUserForRole, setSelectedUserForRole] =
    React.useState<User | null>(null);

  const navigate = useNavigate();
  const [openModal, setOpenModal] = React.useState(false);
  const [modalIsLoading, setModalIsLoading] = React.useState(false);
  const authContext = React.useContext(AuthContext);
  const [modalText, setModalText] = React.useState('');
  const { userRole } = useUserRole();

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    user: User
  ) => {
    setSelectedUser(user);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Omit<User, 'lastKnownSubscription'>
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    requestSort(property, isAsc ? 'desc' : 'asc');
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleUserStatus = async () => {
    const userId = selectedUser?.id.toString() ?? '';
    const email = selectedUser?.email ?? '';
    const active = selectedUser?.active ?? false;
    setModalIsLoading(true);
    setOpenModal(true);
    handleClose();

    active ? setModalText('User Disabled!') : setModalText('User Enabled!');

    await (active ? disableUserTrigger(userId) : enableUserTrigger(userId));

    // deactivate user in cognito userPool
    try {
      await (active
        ? authContext.adminDisableUser(email)
        : authContext.adminEnableUser(email));
    } catch (error) {
      console.error(error);
    }

    // setTimeout so the loading won't disappear too quick making a UI glitch
    setTimeout(() => {
      setModalIsLoading(false);
    }, 350);
  };

  const handleSelectedItem = (item: UserMenuOptions) => {
    if (!users || !selectedUser) return;
    switch (item) {
      case UserMenuOptions.ArchiveUser:
        break;
      case UserMenuOptions.DisableUser: {
        toggleUserStatus();
        break;
      }
      case UserMenuOptions.MoreInfo:
        openUserDetails(selectedUser.id);
        break;
    }
  };

  const openUserDetails = (userId: number) => {
    navigate(Pages.getRoute(Pages.user, userId));
  };

  const emptyRows =
    total > 0
      ? page > 0
        ? Math.max(0, page * rowsPerPage - total)
        : 0
      : rowsPerPage;

  const displayUserRole = (user: User) => {
    const highestRole = extractUserRole(user);
    const role = extractUserRole(user, true);

    let string = '';

    if (highestRole === UserRoles.SUPER_ADMIN) string += 'Super Admin, ';

    // order matters
    if (role === UserRoles.USER && user.organizationName) string += 'Member';
    if (role === UserRoles.CODER) string += 'Coder';
    if (role === UserRoles.ORG_OWNER) string += 'Owner';

    return (
      <Typography
        whiteSpace='normal'
        fontWeight={500}
        color={
          highestRole === UserRoles.SUPER_ADMIN
            ? theme.palette.error.main
            : 'black'
        }
        fontSize={16}
        style={{ marginRight: '4px' }}
      >
        {string}
      </Typography>
    );
  };

  return (
    <Box sx={{ minWidth: 400 }}>
      <TableContainer>
        <Table>
          <AdminTableHead
            onRequestSort={handleRequestSort}
            order={order}
            orderBy={orderBy}
            userRole={userRole}
          />
          {isLoading && (
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: 0 }} colSpan={11}>
                  <CircularProgress
                    color='primary'
                    size='64px'
                    style={{ display: 'block', margin: '24px auto' }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {!isLoading && (
            <TableBody>
              {users.map((user) => (
                <TableRow
                  itemID={`tr-${user.id.toString()}`}
                  key={user.id.toString()}
                  className={'admin-table-row'}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.user, user.id)}
                    >
                      {user.fullName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.user, user.id)}
                    >
                      {' '}
                      {user.email}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.user, user.id)}
                    >
                      {user.phone}
                    </Link>
                  </TableCell>
                  {userRole === UserRoles.SUPER_ADMIN && (
                    <TableCell>
                      <Link
                        className='unstyled-link full-width-block'
                        to={Pages.getRoute(Pages.user, user.id)}
                      >
                        <Typography
                          whiteSpace='normal'
                          fontWeight={500}
                          color={'black'}
                          fontSize={16}
                          style={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          {user.organizationName ?? '-'}
                        </Typography>
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box style={{ display: 'flex' }}>
                      {displayUserRole(user)}
                      {user.organizationName &&
                        !user.accessRoles.includes(UserRoles.ORG_OWNER) && (
                          <KeyboardArrowDownRoundedIcon
                            onClick={() => setSelectedUserForRole(user)}
                          />
                        )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.user, user.id)}
                    >
                      {user.keywordSearchesUsed}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.user, user.id)}
                    >
                      {user.scanSearchesUsed}
                    </Link>
                  </TableCell>
                  {userRole === UserRoles.SUPER_ADMIN && (
                    <TableCell>
                      <Link
                        className='unstyled-link full-width-block'
                        to={Pages.getRoute(Pages.user, user.id)}
                      >
                        {user.purchasedScansAvailable === -1
                          ? 'unlimited'
                          : `${user.purchasedScansAvailable}`}
                      </Link>
                    </TableCell>
                  )}
                  {userRole === UserRoles.SUPER_ADMIN && (
                    <TableCell>
                      <Link
                        className='unstyled-link full-width-block'
                        to={Pages.getRoute(Pages.user, user.id)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          {user.overwriteSubscription && (
                            <Tooltip
                              enterDelay={500}
                              disableFocusListener
                              disableTouchListener
                              title={
                                <Typography color='defaultWhite'>
                                  Subscription overwritten to:{' '}
                                  {_.capitalize(user.overwriteSubscription)}
                                </Typography>
                              }
                            >
                              <div
                                style={{
                                  marginRight: 8,
                                }}
                              >
                                <StarIconBlue />
                              </div>
                            </Tooltip>
                          )}
                          {user.overwriteSubscription
                            ? _.capitalize(user.overwriteSubscription)
                            : user.subscription}
                        </div>
                      </Link>
                    </TableCell>
                  )}

                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.user, user.id)}
                    >
                      {displayDateString(user.lastLogin, false)}
                    </Link>
                  </TableCell>
                  {userRole === UserRoles.SUPER_ADMIN && (
                    <TableCell>
                      <IconButton onClick={(event) => handleClick(event, user)}>
                        <MoreHorizIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell sx={{ border: 0 }} colSpan={5} />
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <PopupMenu
        handleSelectedItem={(item) => handleSelectedItem(item)}
        anchorEl={anchorEl}
        handleClose={handleClose}
        open={open}
        user={selectedUser}
        id={id}
      />
      <InfoModal
        open={openModal}
        loading={modalIsLoading}
        onClose={() => setOpenModal(false)}
        iconSrc={UserIconOrange}
        text={modalText}
      />
      {!isLoading && (
        <PaginationControl
          count={Math.ceil(total / rowsPerPage)}
          onChange={(e, p) => setPage(p)}
          page={page}
        />
      )}
      <Backdrop
        open={!!selectedUserForRole}
        sx={{
          color: '#fff',
          zIndex: 99,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {selectedUserForRole && (
          <ChangeUserRolePopup
            user={selectedUserForRole}
            onClickClose={() => setSelectedUserForRole(null)}
          />
        )}
      </Backdrop>
    </Box>
  );
};

export default UsersTable;
